import { Loader } from "@tandemdiabetes/sugarmate-react-components"
import { useSearch } from "@tanstack/react-router"
import {
  useEffect,
  useState,
} from "react"
import ReactMarkdown from "react-markdown"
import rehypeAutolinkHeadings from "rehype-autolink-headings"
import rehypeExternalLinks from "rehype-external-links"
import rehypeSanitize from "rehype-sanitize"
import remarkHeadingId from "remark-heading-id"

import styles from "./PolicyViewer.module.scss"
import { PublicPageHeader } from "src/components"
import { useConsentFile } from "src/hooks/consent/useConsentFile"
import { validatePolicyParams } from "src/utils/validatePolicyParams"

function supportsGfmRegexRuntime() {
  try {
    // Older Safari versions cannot parse the regex features used by remark-gfm internals.
    new RegExp("(?<name>a)")
    new RegExp("(?<=a)b")

    return true
  } catch {
    return false
  }
}

/* This component and the utilities used are copied from the web-account-module PolicyRenderer component as much as possible */
export function PolicyViewer() {
  const {
    title, file,
  } = useSearch({
    from: "/policy-viewer",
  })

  const {
    parsedTitle, parsedFile,
  } = validatePolicyParams({
    title,
    file,
  })

  const { data: consentContent } = useConsentFile(parsedFile ?? "")
  const [
    remarkPlugins,
    setRemarkPlugins,
  ] = useState([remarkHeadingId])

  useEffect(
    () => {
      let isCancelled = false

      if (!supportsGfmRegexRuntime()) {
        setRemarkPlugins([remarkHeadingId])
        return () => {
          isCancelled = true
        }
      }

      void import("remark-gfm")
        .then((remarkGfmModule) => {
          if (isCancelled) {
            return
          }

          setRemarkPlugins([
            remarkHeadingId,
            remarkGfmModule.default as typeof remarkHeadingId,
          ])
        })
        .catch(() => {
          if (isCancelled) {
            return
          }

          setRemarkPlugins([remarkHeadingId])
        })

      return () => {
        isCancelled = true
      }
    },
    [],
  )

  return (
    <>
      <PublicPageHeader>
        {parsedTitle}
      </PublicPageHeader>
      <div className={styles.container}>
        {!consentContent && (
          <div
            style={{
              margin: "auto",
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <Loader />
          </div>
        )}
        {consentContent && (
          <>
            <div className={styles.markdownContainer}>
              <ReactMarkdown
                remarkPlugins={remarkPlugins}
                rehypePlugins={[
                  rehypeSanitize,
                  rehypeAutolinkHeadings,
                  [
                    rehypeExternalLinks,
                    {
                      target: "_blank",
                      rel: [
                        "noopener",
                        "noreferrer",
                      ],
                      test: (element: {
                        properties: Record<string, unknown>;
                      }) => {
                        const href = element.properties["href"]

                        if (typeof href === "string") {
                          return !href.includes("#")
                        }

                        return false
                      },
                    },
                  ],
                ]}
              >
                {consentContent}
              </ReactMarkdown>
            </div>
          </>
        )}
      </div>
    </>
  )
}
